import React from 'react';
import { MessageSquare, Mail } from 'lucide-react';

/**
 * Chat-style notification prompt asking if the user wants to notify
 * the customer after updating Registration No. or HSRP Informed Date.
 */
export default function NotificationPromptModal({
  notificationTrigger,
  formData,
  onChoice,
}) {
  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="notification-prompt-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-300">
        {/* Chat Header */}
        <div className="p-4" style={{ background: 'linear-gradient(135deg, #4338CA 0%, #4F46E5 50%, #6D28D9 100%)' }}>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg">Update Notification</h3>
              <p className="text-blue-100 text-sm">
                {notificationTrigger === 'registration' ? 'Registration details updated' : 'HSRP information updated'}
              </p>
            </div>
          </div>
        </div>

        {/* Chat Body */}
        <div className="p-5 space-y-4">
          {/* System Message */}
          <div className="flex gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[280px]">
              <p className="text-gray-700 text-sm">
                {notificationTrigger === 'registration'
                  ? `Registration No. ${formData.registration_number} has been updated for ${formData.customer_first_name} ${formData.customer_last_name || ''}.`
                  : `HSRP informed date has been updated to ${formData.hsrp_informed_date} for ${formData.customer_first_name} ${formData.customer_last_name || ''}.`
                }
              </p>
              <p className="text-xs text-gray-500 mt-2">Just now</p>
            </div>
          </div>

          {/* Question Bubble */}
          <div className="flex gap-3">
            <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-4 h-4 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="bg-indigo-50 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[280px]">
              <p className="text-indigo-900 text-sm font-medium">
                Would you like to notify the customer about this update?
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="pt-3 space-y-2">
            <button
              onClick={() => onChoice('whatsapp')}
              data-testid="notify-whatsapp-btn"
              className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-green-500/25"
            >
              <MessageSquare className="w-5 h-5" />
              <span>Send via WhatsApp</span>
            </button>

            <button
              onClick={() => onChoice('email')}
              data-testid="notify-email-btn"
              className="w-full flex items-center justify-center gap-3 px-4 py-3 text-white rounded-xl font-medium shadow-[0_8px_24px_rgba(124,58,237,0.30)] hover:shadow-[0_12px_32px_rgba(124,58,237,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95"
              style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #C026D3 60%, #DB2777 100%)' }}
            >
              <Mail className="w-5 h-5" />
              <span>Send via Email</span>
            </button>

            <button
              onClick={() => onChoice('both')}
              data-testid="notify-both-btn"
              className="w-full flex items-center justify-center gap-3 px-4 py-3 text-white rounded-xl font-medium shadow-[0_8px_24px_rgba(79,70,229,0.30)] hover:shadow-[0_12px_32px_rgba(79,70,229,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95"
              style={{ background: 'linear-gradient(135deg, #4F46E5 0%, #6D28D9 60%, #9333EA 100%)' }}
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span>Send Both</span>
            </button>
          </div>

          {/* Skip Link */}
          <div className="text-center pt-2">
            <button
              onClick={() => onChoice('skip')}
              data-testid="notify-skip-btn"
              className="text-gray-500 hover:text-gray-700 text-sm font-medium transition-colors"
            >
              Skip for now →
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-gray-50 border-t">
          <p className="text-xs text-gray-400 text-center">
            Customer: {formData.mobile_no_1 || formData.whatsapp_no_1 || 'N/A'} • {formData.email || 'No email'}
          </p>
        </div>
      </div>
    </div>
  );
}
